/**
 * Lightweight error logging utility.
 *
 * In development: logs to the console.
 * In production: forwards to Sentry via the @sentry/nextjs SDK.
 *
 * The previous implementation tried to access `window.Sentry` which is never
 * populated when using @sentry/nextjs — the SDK is bundled, not injected as a
 * global. This version imports Sentry directly and works on both client and server.
 */

import * as Sentry from "@sentry/nextjs";

export type ErrorLevel = "error" | "warning" | "info";

export interface ErrorLog {
  id: string;
  message: string;
  level: ErrorLevel;
  stack?: string;
  context?: Record<string, unknown>;
  timestamp: string;
  userAgent?: string;
  url?: string;
}

class ErrorLogger {
  private logs: ErrorLog[] = [];
  private maxLogs = 100;

  log(
    message: string,
    level: ErrorLevel = "error",
    context?: Record<string, unknown>,
    error?: Error,
  ): void {
    const log: ErrorLog = {
      id: `error-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      message,
      level,
      stack: error?.stack,
      context,
      timestamp: new Date().toISOString(),
      userAgent: typeof window !== "undefined" ? window.navigator.userAgent : undefined,
      url: typeof window !== "undefined" ? window.location.href : undefined,
    };

    this.logs.push(log);

    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    if (process.env.NODE_ENV === "development") {
      const logMethod =
        level === "error" ? console.error : level === "warning" ? console.warn : console.info;
      logMethod(`[${level.toUpperCase()}]`, message, context ?? "", error ?? "");
    }

    if (process.env.NODE_ENV === "production") {
      try {
        const sentryLevel =
          level === "error" ? "error" : level === "warning" ? "warning" : "info";

        if (error) {
          Sentry.captureException(error, {
            extra: context,
            level: sentryLevel,
          });
        } else {
          Sentry.captureMessage(message, {
            extra: context,
            level: sentryLevel,
          });
        }
      } catch {
        // Never let error reporting crash the app
      }
    }
  }

  error(message: string, context?: Record<string, unknown>, error?: Error): void {
    this.log(message, "error", context, error);
  }

  warning(message: string, context?: Record<string, unknown>): void {
    this.log(message, "warning", context);
  }

  info(message: string, context?: Record<string, unknown>): void {
    this.log(message, "info", context);
  }

  getLogs(): ErrorLog[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
  }
}

export const errorLogger = new ErrorLogger();
